// NeoGauge — glowing radial gauge for the Overview hero. Successor to
// HeroRing with the same honest geometry: the ring shows TODAY as a share of
// the trailing-7-day DAILY AVERAGE (full ring ≈ a typical day; >100% capped
// visually at 140%). Every element is real telemetry — the center number is
// the actual count (AnimatedCounter), the caption is computed from the ratio,
// and the definition of the metric ships in the `definition` tooltip so the
// owner can see exactly what's being counted.

import { motion } from 'framer-motion';
import AnimatedCounter from '@/components/shared/AnimatedCounter';
import { activityCaption, trendDeltaPct } from '@/utils/overviewQueries';

type NeoGaugeProps = {
  /** Count for the current window (today). */
  value: number;
  /** The "100%" reference (trailing-7d daily average). Zero ⇒ empty ring + no comparison. */
  average: number;
  /** Eyebrow label under the number (e.g. "Captured"). */
  label: string;
  /** Plain-language definition of the metric, shown as a native tooltip. */
  definition: string;
  tone: 'forest' | 'sage';
};

const RADIUS = 52;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const CAP = 1.4;

const TONE: Record<NeoGaugeProps['tone'], { from: string; to: string; glow: string }> = {
  forest: { from: '#1F4437', to: '#2D7D5F', glow: 'rgba(45, 125, 95, 0.45)' },
  sage: { from: '#5F7A56', to: '#8FAE7E', glow: 'rgba(143, 174, 126, 0.5)' },
};

export default function NeoGauge({
  value,
  average,
  label,
  definition,
  tone,
}: NeoGaugeProps): JSX.Element {
  // No-baseline case (average === 0, e.g. a fresh tenant's first day): a
  // full ring + the "New activity today" caption is INTENTIONAL — the ring
  // reads "there is activity", and the caption (not the ring) explains that
  // there's no 7-day baseline yet. Signed off in R1 review 2026-07-02.
  const ratio = average > 0 ? Math.min(value / average, CAP) : value > 0 ? 1 : 0;
  const offset = CIRCUMFERENCE - ratio * CIRCUMFERENCE;
  const { from, to, glow } = TONE[tone];
  const gradId = `neo-gauge-grad-${tone}`;
  const delta = trendDeltaPct(value, average);
  return (
    <div
      className="relative w-[148px] h-[148px] flex items-center justify-center shrink-0"
      title={definition}
    >
      <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120" aria-hidden>
        {/* Neomorphic groove: a light ridge offset under a soft dark track. */}
        <circle
          cx={60}
          cy={61.2}
          r={RADIUS}
          fill="none"
          stroke="rgba(255,255,255,0.9)"
          strokeWidth={7}
        />
        <circle
          cx={60}
          cy={60}
          r={RADIUS}
          fill="none"
          stroke="rgba(163,165,152,0.22)"
          strokeWidth={7}
        />
        <motion.circle
          cx={60}
          cy={60}
          r={RADIUS}
          fill="none"
          stroke={`url(#${gradId})`}
          strokeWidth={7}
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          initial={{ strokeDashoffset: CIRCUMFERENCE }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.1, ease: [0.32, 0.72, 0, 1] }}
          style={{ filter: `drop-shadow(0 0 5px ${glow})` }}
        />
        <defs>
          <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={from} />
            <stop offset="100%" stopColor={to} />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <span
          className="text-[30px] font-serif font-bold text-foreground tracking-tight tabular-nums leading-none"
          aria-label={`${label}: ${value}`}
        >
          <AnimatedCounter value={value} decimals={0} duration={1.1} className="!font-serif" />
        </span>
        <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mt-1.5">
          {label}
        </span>
        <span className="text-[10px] text-muted-foreground/80 mt-1 text-center max-w-[100px] leading-tight">
          {activityCaption(value, average)}
          {delta !== null && value > 0 && (
            <span className={`block font-mono ${delta >= 0 ? 'text-sage' : 'text-copper'}`}>
              {delta >= 0 ? '+' : ''}
              {delta}% vs 7d
            </span>
          )}
        </span>
      </div>
    </div>
  );
}
